import gql from 'graphql-tag';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { CoupledBadge_DisplayFragment } from '~components/displays/__generated__/CoupledBadge.graphql';
import { CoupledIcon, DecoupledIcon } from '~components/ui/icons';
import { IconBadge, Props as IconBadgeProps } from '~components/ui/icons/IconBadge';

export enum CoupledState {
  COUPLED = 'COUPLED',
  DECOUPLED = 'DE-COUPLED',
  RECOUPLED = 'RE-COUPLED',
}

function getTooltipText(
  timestamp: string | null | undefined,
  lastChangedText: string,
): string | undefined {
  if (!timestamp) {
    return undefined;
  }
  const timeAgo = DateTime.fromISO(timestamp).toRelative() ?? '';
  return lastChangedText.replace('{{time}}', timeAgo);
}

export default function CoupledBadge({
  display,
  noTooltip,
  variant,
}: {
  display: CoupledBadge_DisplayFragment;
  noTooltip?: boolean;
} & Pick<IconBadgeProps, 'variant'>) {
  const { t } = useTranslation();
  const coupledState = display?.host?.coupledState as CoupledState | undefined;
  const timestamp = display?.host?.timestamp;

  if (!coupledState) {
    return null;
  }

  const lastChangedText = t('lastChanged', { time: '{{time}}' });

  switch (coupledState) {
    case CoupledState.COUPLED:
      return (
        <IconBadge
          bg="green.50"
          color="green.400"
          icon={CoupledIcon}
          tooltip={noTooltip ? undefined : getTooltipText(timestamp, lastChangedText)}
          variant={variant}
        />
      );
    case CoupledState.DECOUPLED:
      return (
        <IconBadge
          bg="red.50"
          color="red.400"
          icon={DecoupledIcon}
          tooltip={noTooltip ? undefined : getTooltipText(timestamp, lastChangedText)}
          variant={variant}
        />
      );
    case CoupledState.RECOUPLED:
      return (
        <IconBadge
          bg="green.50"
          color="green.400"
          icon={CoupledIcon}
          tooltip={noTooltip ? undefined : getTooltipText(timestamp, lastChangedText)}
          variant={variant}
        />
      );
    default:
      return null;
  }
}

CoupledBadge.graphql = {
  fragments: {
    CoupledBadge_display: gql`
      fragment CoupledBadge_display on Display {
        id
        host {
          coupledState
          timestamp
        }
      }
    `,
  },
};
